/**
 * Keeps Shop Pay Installments visible under the price on VO Product Options pages.
 * VO injects #bcpo into the installment form, pushing messaging below the options.
 */
(function () {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  function bootstrap() {
    const productInfo = document.querySelector('.product-info');
    if (!productInfo) return;

    const paymentTermsBlock = productInfo.querySelector('[data-block-type="payment-terms"]');
    if (!paymentTermsBlock) return;

    const observer = new MutationObserver(() => {
      if (init()) observer.disconnect();
    });

    if (!init()) {
      observer.observe(productInfo, { childList: true, subtree: true });
    }
  }

  function init() {
    const bcpo = document.querySelector('#bcpo');
    if (!bcpo) return false;

    const productInfo = bcpo.closest('.product-info');
    if (!productInfo) return false;

    const paymentTermsBlock = productInfo.querySelector('[data-block-type="payment-terms"]');
    if (!paymentTermsBlock) return false;

    const paymentTermsEl = paymentTermsBlock.querySelector('shopify-payment-terms');
    const installmentForm = paymentTermsBlock.querySelector('payment-terms form, form.shopify-product-form');
    if (!paymentTermsEl || !installmentForm) return false;

    if (productInfo.dataset.voPaymentTermsReady === 'true') return true;
    productInfo.dataset.voPaymentTermsReady = 'true';

    const originalMeta = JSON.parse(paymentTermsEl.getAttribute('shopify-meta') || '{}');
    let addonTotalDollars = 0;
    let syncTimer = null;
    let layoutTimer = null;

    function getInstallmentForm() {
      return paymentTermsBlock.querySelector('payment-terms form, form.shopify-product-form');
    }

    function getPaymentTermsEl() {
      return paymentTermsBlock.querySelector('shopify-payment-terms');
    }

    function getVariantInput() {
      const form = getInstallmentForm();
      return form?.querySelector('[name="id"]');
    }

    function getOrCreateOptionsHost() {
      let host = productInfo.querySelector('.vo-options-host');

      if (!host) {
        host = document.createElement('div');
        host.className = 'vo-options-host product-info__block-item';

        const insertAfter =
          productInfo.querySelector('[data-block-type="separator"]') ||
          paymentTermsBlock;

        insertAfter.insertAdjacentElement('afterend', host);
      }

      return host;
    }

    function detachBcpoFromInstallments() {
      const form = getInstallmentForm();
      const termsEl = getPaymentTermsEl();
      if (!form || !termsEl || !form.contains(bcpo)) return;

      const host = getOrCreateOptionsHost();
      let bcpoForm = host.querySelector('form.vo-bcpo-form');

      if (!bcpoForm) {
        bcpoForm = document.createElement('form');
        bcpoForm.className = 'vo-bcpo-form';
        bcpoForm.action = '/cart/add';
        bcpoForm.method = 'post';
        bcpoForm.enctype = 'multipart/form-data';
        bcpoForm.autocomplete = 'off';
        host.appendChild(bcpoForm);
      }

      const bcpoValidation = form.querySelector('.bcpo-validation');
      bcpoForm.appendChild(bcpo);
      if (bcpoValidation) bcpoForm.appendChild(bcpoValidation);

      let variantInput = form.querySelector('[name="id"]');
      if (!variantInput) {
        variantInput = document.createElement('input');
        variantInput.type = 'hidden';
        variantInput.name = 'id';
        form.prepend(variantInput);
      }

      if (!form.contains(termsEl)) {
        form.appendChild(termsEl);
      } else {
        variantInput.insertAdjacentElement('afterend', termsEl);
      }

      form.querySelectorAll('[name="id"]').forEach((input, index) => {
        if (index > 0) input.remove();
      });
    }

    function applyLayout() {
      detachBcpoFromInstallments();

      const priceBlock = productInfo.querySelector('[data-block-type="price"]');
      if (priceBlock && priceBlock.nextElementSibling !== paymentTermsBlock) {
        priceBlock.insertAdjacentElement('afterend', paymentTermsBlock);
      }

      const termsEl = getPaymentTermsEl();
      paymentTermsBlock.style.setProperty('display', 'block', 'important');
      termsEl?.style.setProperty('display', 'block', 'important');
      termsEl?.style.setProperty('visibility', 'visible', 'important');
      termsEl?.style.setProperty('opacity', '1', 'important');
    }

    function scheduleLayout() {
      clearTimeout(layoutTimer);
      layoutTimer = setTimeout(applyLayout, 0);
    }

    function scheduleSync(options) {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(() => syncPaymentTerms(options), 50);
    }

    function getBcpoVariantId() {
      return document.querySelector('#bcpo-select')?.value;
    }

    function parseMoneyToCents(text) {
      if (!text) return null;
      const match = String(text).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
      if (!match) return null;
      return Math.round(parseFloat(match[1]) * 100);
    }

    function formatMoney(cents) {
      const amount = (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `$${amount}`;
    }

    function getVariantFromMeta(variantId, meta) {
      return meta.variants?.find((v) => String(v.id) === String(variantId));
    }

    function getVariantBaseCents(variantId) {
      const variant = getVariantFromMeta(variantId, originalMeta);
      if (!variant?.full_price) return null;
      return parseMoneyToCents(variant.full_price.replace(/<[^>]*>/g, ''));
    }

    function getDisplayedTotalCents() {
      const priceBlock = productInfo.querySelector('[data-block-type="price"]');
      const saleEl = priceBlock?.querySelector('.price--sale') || priceBlock?.querySelector('sale-price');
      return parseMoneyToCents(saleEl?.textContent);
    }

    function syncPaymentTerms(options = {}) {
      applyLayout();

      const termsEl = getPaymentTermsEl();
      const variantInput = getVariantInput();
      if (!termsEl || !variantInput) return;

      const variantId = getBcpoVariantId();
      if (!variantId) return;

      if (typeof options.addonTotalDollars === 'number') {
        addonTotalDollars = options.addonTotalDollars;
      }

      termsEl.setAttribute('shopify-meta', JSON.stringify(originalMeta));

      const baseCents = getVariantBaseCents(variantId);
      let totalCents = baseCents;

      if (addonTotalDollars > 0 && baseCents != null) {
        totalCents = baseCents + Math.round(addonTotalDollars * 100);
      } else {
        const displayed = getDisplayedTotalCents();
        if (displayed != null && baseCents != null && displayed !== baseCents) {
          totalCents = displayed;
        }
      }

      if (totalCents != null && baseCents != null && totalCents !== baseCents) {
        const meta = JSON.parse(termsEl.getAttribute('shopify-meta') || '{}');
        const variant = getVariantFromMeta(variantId, meta);
        if (variant) {
          variant.full_price = `<span class=transcy-money>${formatMoney(totalCents)}</span>`;
          if (variant.number_of_payment_terms) {
            const perTermCents = Math.round(totalCents / variant.number_of_payment_terms);
            variant.price_per_term = `<span class=transcy-money>${formatMoney(perTermCents)}</span>`;
          }
          termsEl.setAttribute('shopify-meta', JSON.stringify(meta));
        }
      }

      if (variantInput.value !== variantId) {
        variantInput.value = variantId;
      }

      termsEl.setAttribute('variant-id', variantId);
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    applyLayout();

    [100, 300, 600, 1000].forEach((delay) => {
      setTimeout(() => {
        applyLayout();
        syncPaymentTerms();
      }, delay);
    });

    document.querySelector('#bcpo-select')?.addEventListener('change', () => {
      addonTotalDollars = 0;
      scheduleSync();
    });

    bcpo.querySelectorAll('.single-option-selector').forEach((select) => {
      select.addEventListener('change', () => {
        addonTotalDollars = 0;
        scheduleSync();
      });
    });

    bcpo.querySelectorAll('input[type="checkbox"][data-price]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => scheduleSync());
    });

    function onAddonPriceChange(_event, pricingTotal) {
      const addon = typeof pricingTotal === 'number' ? pricingTotal : parseFloat(pricingTotal) || 0;
      scheduleSync({ addonTotalDollars: addon });
    }

    if (window.jQuery) {
      window.jQuery(document.body).on('productOptionsPriceChange', onAddonPriceChange);
    }

    document.body.addEventListener('productOptionsPriceChange', (event) => {
      const detail = event.detail;
      const addon = Array.isArray(detail) ? detail[0] : detail?.pricingTotal ?? detail;
      onAddonPriceChange(event, addon);
    });

    const priceBlock = productInfo.querySelector('[data-block-type="price"]');
    if (priceBlock) {
      new MutationObserver(() => scheduleSync()).observe(priceBlock, {
        subtree: true,
        characterData: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-amount', 'data-usd'],
      });
    }

    const form = getInstallmentForm();
    if (form) {
      new MutationObserver(() => scheduleLayout()).observe(form, {
        childList: true,
      });
    }

    scheduleSync();
    return true;
  }
})();
